import {
  Alert,
  Button,
  Card,
  Group,
  List,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, call, type Schemas } from "../api/client";
import { useMe } from "../lib/auth";
import { grams, isoDate, kcal } from "../lib/format";
import {
  DEFAULT_PACE,
  cmToFeetInches,
  feetInchesToCm,
  formatWeight,
  paceOptions,
  paceToKgPerWeek,
  useUnits,
  weightToKg,
  weightUnit,
} from "../lib/units";

const ACTIVITY = [
  { value: "sedentary", label: "Sedentary (little exercise)" },
  { value: "light", label: "Light (1–3 days/week)" },
  { value: "moderate", label: "Moderate (3–5 days/week)" },
  { value: "active", label: "Active (6–7 days/week)" },
  { value: "very_active", label: "Very active (physical job or 2×/day)" },
];
type GoalForm = { effective_from: string; kcal: number | string; protein_g: number | string; carbs_g: number | string; fat_g: number | string };

export function GoalsPage() {
  const queryClient = useQueryClient();
  const goals = useQuery({ queryKey: ["goals"], queryFn: () => call(api.GET("/api/v1/goals")) });
  const current = goals.data?.find((g) => g.effective_from <= isoDate(new Date()));
  const [form, setForm] = useState<GoalForm | null>(null);
  const shown: GoalForm = form ?? {
    effective_from: isoDate(new Date()),
    kcal: current?.kcal ?? "",
    protein_g: current?.protein_g ?? "",
    carbs_g: current?.carbs_g ?? "",
    fat_g: current?.fat_g ?? "",
  };
  const num = (v: number | string) => (v === "" ? null : Number(v));
  const save = useMutation({
    mutationFn: () =>
      call(
        api.PUT("/api/v1/goals/{effective_from}", {
          params: { path: { effective_from: shown.effective_from } },
          body: { kcal: Number(shown.kcal), protein_g: num(shown.protein_g), carbs_g: num(shown.carbs_g), fat_g: num(shown.fat_g) },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      queryClient.invalidateQueries({ queryKey: ["summary"] });
      setForm(null);
      notifications.show({ color: "teal", message: "Goal saved" });
    },
  });
  const set = (key: keyof GoalForm, value: number | string) => setForm({ ...shown, [key]: value });

  return (
    <Stack>
      <Title order={3}>Goals</Title>
      <Card withBorder>
        <Stack>
          <Text fw={600}>Daily goal</Text>
          <SimpleGrid cols={{ base: 2, sm: 5 }}>
            <TextInput type="date" label="Starting" value={shown.effective_from} onChange={(e) => set("effective_from", e.currentTarget.value)} />
            <NumberInput label="kcal" min={1} value={shown.kcal} onChange={(v) => set("kcal", v)} hideControls />
            <NumberInput label="Protein (g)" min={0} value={shown.protein_g} onChange={(v) => set("protein_g", v)} hideControls />
            <NumberInput label="Carbs (g)" min={0} value={shown.carbs_g} onChange={(v) => set("carbs_g", v)} hideControls />
            <NumberInput label="Fat (g)" min={0} value={shown.fat_g} onChange={(v) => set("fat_g", v)} hideControls />
          </SimpleGrid>
          <Group justify="flex-end">
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={shown.kcal === "" || !shown.effective_from}>
              Save goal
            </Button>
          </Group>
        </Stack>
      </Card>

      <Calculator
        onUse={(r) =>
          setForm({ ...shown, kcal: r.kcal, protein_g: r.protein_g, carbs_g: r.carbs_g, fat_g: r.fat_g })
        }
      />

      {goals.data && goals.data.length > 0 && (
        <Card withBorder padding={0}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>From</Table.Th>
                <Table.Th ta="right">kcal</Table.Th>
                <Table.Th ta="right">Protein</Table.Th>
                <Table.Th ta="right">Carbs</Table.Th>
                <Table.Th ta="right">Fat</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {goals.data.map((g) => (
                <Table.Tr key={g.effective_from}>
                  <Table.Td>{g.effective_from}</Table.Td>
                  <Table.Td ta="right">{kcal(g.kcal)}</Table.Td>
                  <Table.Td ta="right">{grams(g.protein_g)}</Table.Td>
                  <Table.Td ta="right">{grams(g.carbs_g)}</Table.Td>
                  <Table.Td ta="right">{grams(g.fat_g)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
    </Stack>
  );
}

function Calculator({ onUse }: { onUse: (r: Schemas["RecommendationOut"]) => void }) {
  const me = useMe();
  const units = useUnits();
  const queryClient = useQueryClient();
  const [edits, setEdits] = useState<Record<string, string | number | null>>({});
  const profile = me.data;
  const value = (key: string, fallback: string | number | null | undefined) =>
    key in edits ? edits[key] : (fallback ?? null);
  const sex = value("sex", profile?.sex) as string | null;
  const birth = value("birth_date", profile?.birth_date) as string | null;
  const height = value("height_cm", profile?.height_cm);
  const weight = value("weight_kg", null);
  const activity = value("activity_level", profile?.activity_level) as string | null;
  // Pace is chosen in the user's units; switching units picks that system's default.
  const paces = paceOptions(units);
  const chosenPace = value("pace", null) as string | null;
  const pace = paces.some((p) => p.value === chosenPace) ? chosenPace! : DEFAULT_PACE[units];

  const calc = useMutation({
    mutationFn: async () => {
      // Remember the profile so the calculator (and agents) can use it next time.
      await call(
        api.PATCH("/api/v1/me", {
          body: {
            sex: sex as Schemas["Sex"] | null,
            birth_date: birth || null,
            height_cm: height === "" || height == null ? null : Number(height),
            activity_level: activity as Schemas["ActivityLevel"] | null,
          },
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["me"] });
      return call(
        api.POST("/api/v1/goals/recommendation", {
          body: {
            weight_kg: weight === "" || weight == null ? null : weightToKg(Number(weight), units),
            goal_kg_per_week: paceToKgPerWeek(Number(pace), units),
          },
        }),
      );
    },
    meta: { errorTitle: "Couldn't calculate" },
  });
  const r = calc.data;
  const set = (key: string) => (v: string | number | null) => setEdits({ ...edits, [key]: v });

  return (
    <Card withBorder>
      <Stack>
        <div>
          <Text fw={600}>Calculate a recommendation</Text>
          <Text size="sm" c="dimmed">
            Uses the Mifflin-St Jeor formula. Your answers are saved to your profile. Leave
            weight empty to use your latest logged weight.
          </Text>
        </div>
        <SimpleGrid cols={{ base: 1, xs: 2, sm: 3 }}>
          <Select label="Sex (for the formula)" data={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }]} value={sex} onChange={set("sex")} />
          <TextInput type="date" label="Birth date" value={birth ?? ""} onChange={(e) => set("birth_date")(e.currentTarget.value)} />
          {units === "imperial" ? (
            <HeightFeetInches cm={height === "" || height == null ? null : Number(height)} onChange={set("height_cm")} />
          ) : (
            <NumberInput label="Height (cm)" min={50} max={300} value={height ?? ""} onChange={set("height_cm")} hideControls />
          )}
          <NumberInput label={`Weight (${weightUnit(units)})`} placeholder="latest logged" min={1} max={1100} value={weight ?? ""} onChange={set("weight_kg")} hideControls />
          <Select label="Activity" data={ACTIVITY} value={activity} onChange={set("activity_level")} />
          <Select label="Pace" data={paces} value={pace} onChange={set("pace")} allowDeselect={false} />
        </SimpleGrid>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => calc.mutate()} loading={calc.isPending}>
            Calculate
          </Button>
        </Group>
        {r && (
          <Alert color="teal" title={`Recommended: ${kcal(r.kcal)} kcal/day`}>
            <Stack gap="xs">
              <Text size="sm">
                Protein {grams(r.protein_g)} · Carbs {grams(r.carbs_g)} · Fat {grams(r.fat_g)} (BMR{" "}
                {kcal(r.bmr_kcal)}, maintenance {kcal(r.maintenance_kcal)}, weight{" "}
                {formatWeight(r.inputs.weight_kg, units)})
              </Text>
              <List size="sm">
                {r.notes.map((n) => (
                  <List.Item key={n}>{n}</List.Item>
                ))}
              </List>
              <Button size="xs" w="fit-content" onClick={() => onUse(r)}>
                Use these values
              </Button>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

function HeightFeetInches({ cm, onChange }: { cm: number | null; onChange: (cm: number | null) => void }) {
  const { feet, inches } = cm == null ? { feet: null, inches: null } : cmToFeetInches(cm);
  const update = (f: number | string | null, i: number | string | null) => {
    if ((f === "" || f == null) && (i === "" || i == null)) return onChange(null);
    onChange(feetInchesToCm(Number(f || 0), Number(i || 0)));
  };
  return (
    <SimpleGrid cols={2} spacing="xs">
      <NumberInput label="Height (ft)" min={1} max={9} value={feet ?? ""} onChange={(v) => update(v, inches)} hideControls />
      <NumberInput label="(in)" min={0} max={11} value={inches ?? ""} onChange={(v) => update(feet, v)} hideControls />
    </SimpleGrid>
  );
}
